// store/serial.js

// 串口单例实例（模块级变量，确保全局唯一）
let serialPortInstance = null
let serialReaderInstance = null
let currentWriterInstance = null
let isReadingInstance = false
let serialConnectedInstance = false
let deviceInfoInstance = null

const state = {
  globalSerialPort: null,
  globalSerialConnected: false,
  deviceInfo: null,
  isReading: false,
  isWriting: false,
  shouldCleanup: false
}

const mutations = {
  SET_GLOBAL_SERIAL_PORT(state, port) {
    state.globalSerialPort = port
    serialPortInstance = port
  },
  SET_GLOBAL_SERIAL_CONNECTED(state, connected) {
    state.globalSerialConnected = connected
    serialConnectedInstance = connected
  },
  SET_DEVICE_INFO(state, info) {
    state.deviceInfo = info
    deviceInfoInstance = info
  },
  SET_IS_READING(state, reading) {
    state.isReading = reading
    isReadingInstance = reading
  },
  SET_IS_WRITING(state, writing) {
    state.isWriting = writing
  },
  SET_SERIAL_READER(state, reader) {
    serialReaderInstance = reader
  },
  SET_SERIAL_WRITER(state, writer) {
    currentWriterInstance = writer
  },
  SET_SHOULD_CLEANUP(state, value) {
    state.shouldCleanup = value
  }
}

const actions = {
  setGlobalSerialPort({ commit }, port) {
    commit('SET_GLOBAL_SERIAL_PORT', port)
  },
  setGlobalSerialConnected({ commit }, connected) {
    commit('SET_GLOBAL_SERIAL_CONNECTED', connected)
  },
  setDeviceInfo({ commit }, info) {
    commit('SET_DEVICE_INFO', info)
  },
  setIsReading({ commit }, reading) {
    commit('SET_IS_READING', reading)
  },
  setIsWriting({ commit }, writing) {
    commit('SET_IS_WRITING', writing)
  },
  setSerialReader({ commit }, reader) {
    commit('SET_SERIAL_READER', reader)
  },
  setSerialWriter({ commit }, writer) {
    commit('SET_SERIAL_WRITER', writer)
  },
  setShouldCleanup({ commit }, value) {
    commit('SET_SHOULD_CLEANUP', value)
  },

  /**
   * 检查端口是否打开
   */
  async isPortOpen() {
    if (!serialPortInstance) return false
    try {
      if (serialPortInstance.readable || serialPortInstance.writable) {
        return true
      }
      return false
    } catch (e) {
      return false
    }
  },

  /**
   * 检查串口连接是否健康
   */
  isSerialHealthy() {
    if (!serialPortInstance) return false
    try {
      if (serialPortInstance.readable) {
        if (serialPortInstance.readable.locked) {
          return isReadingInstance && serialReaderInstance !== null
        }
        return true
      }
      return false
    } catch (e) {
      return false
    }
  },

  /**
   * 彻底清理串口资源
   */
  async cleanupSerialResources({ commit, dispatch }, keepPort = false) {
    console.log('清理串口资源...')

    isReadingInstance = false
    commit('SET_IS_READING', false)
    commit('SET_IS_WRITING', false)

    if (serialReaderInstance) {
      try {
        await serialReaderInstance.cancel()
      } catch (e) {
        console.warn('取消 reader 失败:', e)
      }
      try {
        serialReaderInstance.releaseLock()
      } catch (e) {
        console.warn('释放 reader 锁失败:', e)
      }
      serialReaderInstance = null
      commit('SET_SERIAL_READER', null)
    }

    if (currentWriterInstance) {
      try {
        await currentWriterInstance.abort()
      } catch (e) {
        console.warn('abort writer 失败:', e)
      }
      try {
        currentWriterInstance.releaseLock()
      } catch (e) {
        console.warn('释放 writer 锁失败:', e)
      }
      currentWriterInstance = null
      commit('SET_SERIAL_WRITER', null)
    }

    if (serialPortInstance && serialPortInstance.readable) {
      try {
        if (serialPortInstance.readable.locked) {
          const reader = serialPortInstance.readable.getReader()
          reader.releaseLock()
        }
      } catch (e) {
        console.warn('释放 readable 流失败:', e)
      }
    }

    if (serialPortInstance && serialPortInstance.writable) {
      try {
        if (serialPortInstance.writable.locked) {
          const writer = serialPortInstance.writable.getWriter()
          await writer.abort()
          writer.releaseLock()
        }
      } catch (e) {
        console.warn('释放 writable 流失败:', e)
      }
    }

    if (serialPortInstance) {
      try {
        const isOpen = await dispatch('isPortOpen')
        if (isOpen) {
          await serialPortInstance.close()
          console.log('串口已关闭')
        }
      } catch (e) {
        console.warn('关闭串口失败:', e)
      }
    }

    serialConnectedInstance = false
    commit('SET_GLOBAL_SERIAL_CONNECTED', false)

    if (!keepPort) {
      serialPortInstance = null
      commit('SET_GLOBAL_SERIAL_PORT', null)
    }

    commit('SET_DEVICE_INFO', null)
    commit('SET_IS_READING', false)
    commit('SET_IS_WRITING', false)

    return true
  },

  /**
   * 连接串口（单例模式）
   */
  async connectSerial({ commit, dispatch }, { port, deviceInfo }) {
    if (serialConnectedInstance) {
      console.warn('已有串口连接，先断开旧连接')
      await dispatch('disconnectSerial')
    }

    serialPortInstance = port
    commit('SET_GLOBAL_SERIAL_PORT', port)
    commit('SET_DEVICE_INFO', deviceInfo)

    try {
      const isOpen = await dispatch('isPortOpen')

      if (isOpen) {
        serialConnectedInstance = true
        commit('SET_GLOBAL_SERIAL_CONNECTED', true)
        return true
      }

      await dispatch('cleanupSerialResources', true)
      await new Promise(resolve => setTimeout(resolve, 300))

      await serialPortInstance.open({ baudRate: 115200 })

      serialConnectedInstance = true
      commit('SET_GLOBAL_SERIAL_CONNECTED', true)
      return true
    } catch (error) {
      console.error('连接串口失败:', error)
      serialConnectedInstance = false
      commit('SET_GLOBAL_SERIAL_CONNECTED', false)
      serialPortInstance = null
      commit('SET_GLOBAL_SERIAL_PORT', null)
      commit('SET_DEVICE_INFO', null)
      throw error
    }
  },

  /**
   * 断开串口（彻底关闭）
   */
  async disconnectSerial({ commit, dispatch }) {
    console.log('断开串口连接...')

    try {
      await dispatch('cleanupSerialResources', false)
      console.log('串口已断开')
      return true
    } catch (error) {
      console.error('断开串口失败:', error)
      serialConnectedInstance = false
      commit('SET_GLOBAL_SERIAL_CONNECTED', false)
      serialPortInstance = null
      commit('SET_GLOBAL_SERIAL_PORT', null)
      serialReaderInstance = null
      commit('SET_SERIAL_READER', null)
      currentWriterInstance = null
      commit('SET_SERIAL_WRITER', null)
      isReadingInstance = false
      commit('SET_IS_READING', false)
      commit('SET_IS_WRITING', false)
      commit('SET_DEVICE_INFO', null)
      throw error
    }
  },

  /**
   * 获取串口实例
   */
  getSerialPort() {
    return serialPortInstance
  },

  /**
   * 获取连接状态
   */
  isSerialConnected() {
    return serialConnectedInstance
  },

  /**
   * 获取设备信息
   */
  getDeviceInfo() {
    return deviceInfoInstance
  },

  /**
   * 获取读取器
   */
  getSerialReader() {
    return serialReaderInstance
  },

  /**
   * 获取写入器
   */
  getSerialWriter() {
    return currentWriterInstance
  },

  /**
   * 完全重置串口状态（用于退出登录）
   */
  async fullReset({ commit, dispatch }) {
    console.log('执行串口完全重置...')

    await dispatch('disconnectSerial')

    serialConnectedInstance = false
    commit('SET_GLOBAL_SERIAL_CONNECTED', false)
    serialPortInstance = null
    commit('SET_GLOBAL_SERIAL_PORT', null)
    serialReaderInstance = null
    commit('SET_SERIAL_READER', null)
    currentWriterInstance = null
    commit('SET_SERIAL_WRITER', null)
    isReadingInstance = false
    commit('SET_IS_READING', false)
    commit('SET_IS_WRITING', false)
    commit('SET_DEVICE_INFO', null)

    try {
      sessionStorage.removeItem('serialBurnToolCache')
    } catch (e) {}

    console.log('串口完全重置完成')
    return true
  }
}

const getters = {
  globalSerialPort: state => state.globalSerialPort,
  globalSerialConnected: state => state.globalSerialConnected,
  deviceInfo: state => state.deviceInfo,
  isReading: state => state.isReading,
  isWriting: state => state.isWriting,
  shouldCleanup: state => state.shouldCleanup,
  actualSerialConnected: () => serialConnectedInstance,
  actualSerialPort: () => serialPortInstance
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
